import React, { useState, useRef } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate } from 'react-router-dom';
import { bomberosCompaniaAPI, bomberosImportacionAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Upload,
    FileSpreadsheet,
    Users,
    HardHat,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Download,
    Loader2,
    FileDown,
    Undo2,
    RefreshCw,
} from 'lucide-react';
import * as XLSX from 'xlsx';

const IMPORT_TYPES = {
    personal: {
        label: 'Personal / Bomberos',
        icon: Users,
        fields: ['rut', 'nombres', 'apellido_paterno', 'apellido_materno', 'fecha_nacimiento', 'fecha_ingreso', 'grado', 'cargo', 'email', 'telefono', 'direccion', 'grupo_sanguineo', 'contacto_emergencia', 'telefono_emergencia'],
        required: ['rut', 'nombres', 'apellido_paterno'],
        template: [
            { rut: '12.345.678-9', nombres: 'Juan Carlos', apellido_paterno: 'Pérez', apellido_materno: 'González', fecha_nacimiento: '1990-01-15', fecha_ingreso: '2015-03-20', grado: 'Bombero', cargo: 'Maquinista', email: 'juan@email.com', telefono: '+56912345678', direccion: 'Calle 123', grupo_sanguineo: 'O+', contacto_emergencia: 'María Pérez', telefono_emergencia: '+56911111111' },
            { rut: '98.765.432-1', nombres: 'María Fernanda', apellido_paterno: 'López', apellido_materno: 'Silva', fecha_nacimiento: '1985-06-20', fecha_ingreso: '2010-05-15', grado: 'Teniente', cargo: 'Oficial', email: 'maria@email.com', telefono: '+56987654321', direccion: 'Avenida 456', grupo_sanguineo: 'A+', contacto_emergencia: '', telefono_emergencia: '' },
        ]
    },
    epp: {
        label: 'EPP / Material Menor',
        icon: HardHat,
        fields: ['codigo', 'tipo', 'marca', 'modelo', 'numero_serie', 'talla', 'fecha_adquisicion', 'fecha_vencimiento', 'estado'],
        required: ['tipo', 'marca'],
        template: [
            { codigo: 'EPP-001', tipo: 'casco', marca: 'MSA', modelo: 'Gallet F1', numero_serie: 'SN12345', talla: 'M', fecha_adquisicion: '2024-01-15', fecha_vencimiento: '2029-01-15', estado: 'disponible' },
            { codigo: 'EPP-002', tipo: 'chaqueton', marca: 'Globe', modelo: 'G-XTREME', numero_serie: 'SN67890', talla: 'L', fecha_adquisicion: '2024-02-20', fecha_vencimiento: '2029-02-20', estado: 'disponible' },
        ]
    },
};

export default function BomberosImportacionPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const fileInputRef = useRef(null);
    
    const [importType, setImportType] = useState('personal');
    const [companiaId, setCompaniaId] = useState('');
    const [companias, setCompanias] = useState([]);
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [validationResults, setValidationResults] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importResults, setImportResults] = useState(null);
    const [lastImportIds, setLastImportIds] = useState([]);
    const [reverting, setReverting] = useState(false);

    React.useEffect(() => {
        loadCompanias();
    }, []);

    const loadCompanias = async () => {
        try {
            const res = await bomberosCompaniaAPI.getAll();
            setCompanias(res.data);
            if (res.data.length > 0) {
                setCompaniaId(res.data[0].id);
            }
        } catch (err) {
            toast.error('Error al cargar compañías');
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        if (!selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
            toast.error('Por favor seleccione un archivo Excel (.xlsx, .xls) o CSV');
            return;
        }

        setFile(selectedFile);
        parseFile(selectedFile);
    };

    const parseFile = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                
                setPreviewData(jsonData);
                validateData(jsonData);
            } catch (err) {
                console.error('Error parsing file:', err);
                toast.error('Error al leer el archivo');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const validateData = (data) => {
        const config = IMPORT_TYPES[importType];
        const results = data.map((row, idx) => {
            const errors = [];
            const warnings = [];
            
            if (importType === 'personal') {
                // Validar RUT siempre requerido
                if (!row.rut || String(row.rut).trim() === '') {
                    errors.push('Campo requerido "rut" vacío');
                } else if (!validateRut(row.rut)) {
                    warnings.push('RUT podría ser inválido');
                }
                
                // Aceptar campos separados O nombre_completo
                const tieneNombres = row.nombres && String(row.nombres).trim() !== '';
                const tieneApellido = row.apellido_paterno && String(row.apellido_paterno).trim() !== '';
                const tieneNombreCompleto = row.nombre_completo && String(row.nombre_completo).trim() !== '';
                
                if (!tieneNombres && !tieneNombreCompleto) {
                    errors.push('Se requiere "nombres" y "apellido_paterno" (o "nombre_completo")');
                }
                if (tieneNombres && !tieneApellido) {
                    warnings.push('Falta "apellido_paterno"');
                }
                
                if (row.email && !String(row.email).includes('@')) {
                    warnings.push('Email podría ser inválido');
                }
            } else {
                // Validación genérica para otros tipos
                config.required.forEach(field => {
                    if (!row[field] || String(row[field]).trim() === '') {
                        errors.push(`Campo requerido "${field}" vacío`);
                    }
                });
            }
            
            return {
                row: idx + 1,
                data: row,
                valid: errors.length === 0,
                errors,
                warnings,
            };
        });
        
        setValidationResults(results);
    };

    const validateRut = (rut) => {
        if (!rut) return false;
        const cleanRut = String(rut).replace(/[.-]/g, '').toUpperCase();
        return cleanRut.length >= 8 && cleanRut.length <= 9;
    };

    const handleImport = async () => {
        const validRows = validationResults.filter(r => r.valid);
        if (validRows.length === 0) {
            toast.error('No hay filas válidas para importar');
            return;
        }

        if (!companiaId) {
            toast.error('Seleccione una compañía');
            return;
        }

        setImporting(true);
        setImportProgress(0);
        
        try {
            // Prepare data - clean up dates and format
            const registros = validRows.map(row => {
                const data = { ...row.data };
                
                // Clean up dates
                if (data.fecha_nacimiento && typeof data.fecha_nacimiento === 'number') {
                    data.fecha_nacimiento = excelDateToISO(data.fecha_nacimiento);
                }
                if (data.fecha_ingreso && typeof data.fecha_ingreso === 'number') {
                    data.fecha_ingreso = excelDateToISO(data.fecha_ingreso);
                }
                if (data.fecha_adquisicion && typeof data.fecha_adquisicion === 'number') {
                    data.fecha_adquisicion = excelDateToISO(data.fecha_adquisicion);
                }
                if (data.fecha_vencimiento && typeof data.fecha_vencimiento === 'number') {
                    data.fecha_vencimiento = excelDateToISO(data.fecha_vencimiento);
                }
                
                return data;
            });
            
            setImportProgress(30);
            
            // Use the new bulk import endpoint
            const response = await bomberosImportacionAPI.importar(importType, registros, companiaId);
            
            setImportProgress(100);
            
            setImportResults({
                success: response.data.success,
                failed: response.data.failed,
                updated: response.data.updated || 0,
                errors: response.data.errors || [],
                skipped_details: response.data.skipped_details || [],
            });
            setLastImportIds(response.data.ids_creados || []);
            
            if (response.data.success > 0) {
                toast.success(`Se importaron ${response.data.success} registros nuevos`);
            }
            if (response.data.updated > 0) {
                toast.info(`Se actualizaron ${response.data.updated} registros existentes`);
            }
            if (response.data.failed > 0) {
                toast.error(`${response.data.failed} registros fallaron`);
            }
        } catch (err) {
            console.error('Error importing:', err);
            toast.error(`Error al importar: ${err.response?.data?.detail || err.message}`);
            setImportResults({
                success: 0,
                failed: validRows.length,
                errors: [err.response?.data?.detail || err.message]
            });
        } finally {
            setImporting(false);
        }
    };

    const excelDateToISO = (excelDate) => {
        const date = new Date((excelDate - 25569) * 86400 * 1000);
        return date.toISOString().split('T')[0];
    };

    const downloadTemplate = () => {
        const config = IMPORT_TYPES[importType];
        const ws = XLSX.utils.json_to_sheet(config.template);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
        XLSX.writeFile(wb, `plantilla_${importType}.xlsx`);
        toast.success('Plantilla descargada');
    };

    const resetImport = () => {
        setFile(null);
        setPreviewData([]);
        setValidationResults([]);
        setImportResults(null);
        setImportProgress(0);
        setLastImportIds([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRevertir = async () => {
        if (lastImportIds.length === 0) {
            toast.error('No hay importación para revertir');
            return;
        }
        if (!window.confirm(`¿Revertir la última importación? Se eliminarán ${lastImportIds.length} registros.`)) return;
        if (!window.confirm('Esta acción NO se puede deshacer. ¿Confirmar?')) return;
        
        setReverting(true);
        try {
            const res = await bomberosImportacionAPI.revertir(lastImportIds, importType);
            toast.success(res.data.message);
            setLastImportIds([]);
            setImportResults(null);
            resetImport();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al revertir');
        } finally {
            setReverting(false);
        }
    };

    const config = IMPORT_TYPES[importType];
    const validCount = validationResults.filter(r => r.valid).length;
    const invalidCount = validationResults.filter(r => !r.valid).length;

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Upload className="h-6 w-6 text-green-500" />
                        Importación Masiva
                    </h1>
                    <p className="text-muted-foreground">Carga datos desde archivos Excel o CSV</p>
                </div>
            </div>

            {/* Step 1: Select Type */}
            <Card className="border-2">
                <CardHeader>
                    <CardTitle>Paso 1: Seleccionar Tipo de Datos</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(IMPORT_TYPES).map(([key, typeConfig]) => {
                            const Icon = typeConfig.icon;
                            return (
                                <Card
                                    key={key}
                                    className={`cursor-pointer transition-all ${importType === key ? 'border-2 border-primary bg-primary/5' : 'border hover:border-primary/50'}`}
                                    onClick={() => {
                                        setImportType(key);
                                        resetImport();
                                    }}
                                >
                                    <CardContent className="pt-6 flex items-center gap-4">
                                        <div className={`p-3 rounded-lg ${importType === key ? 'bg-primary text-white' : 'bg-muted'}`}>
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold">{typeConfig.label}</h3>
                                            <p className="text-sm text-muted-foreground">
                                                Campos: {typeConfig.fields.slice(0, 4).join(', ')}...
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                    
                    <div className="flex items-center gap-4 mt-6">
                        <div className="flex-1">
                            <label className="text-sm font-medium mb-2 block">Compañía destino</label>
                            <Select value={companiaId} onValueChange={setCompaniaId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar compañía" />
                                </SelectTrigger>
                                <SelectContent>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="pt-6">
                            <Button variant="outline" onClick={downloadTemplate}>
                                <FileDown className="h-4 w-4 mr-2" />
                                Descargar Plantilla
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Step 2: Upload File */}
            <Card className="border-2">
                <CardHeader>
                    <CardTitle>Paso 2: Cargar Archivo</CardTitle>
                    <CardDescription>Formatos soportados: .xlsx, .xls, .csv</CardDescription>
                </CardHeader>
                <CardContent>
                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${file ? 'border-green-500 bg-green-50' : 'border-muted-foreground/25 hover:border-primary/50'}`}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="hidden"
                            onChange={handleFileSelect}
                        />
                        {file ? (
                            <div className="flex items-center justify-center gap-3">
                                <FileSpreadsheet className="h-10 w-10 text-green-600" />
                                <div className="text-left">
                                    <p className="font-medium">{file.name}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {previewData.length} filas encontradas
                                    </p>
                                </div>
                                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); resetImport(); }}>
                                    Cambiar
                                </Button>
                            </div>
                        ) : (
                            <>
                                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                <p className="text-lg font-medium">Arrastra un archivo aquí o haz clic para seleccionar</p>
                                <p className="text-sm text-muted-foreground mt-2">Máximo 1000 filas por importación</p>
                            </>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Step 3: Validation & Preview */}
            {validationResults.length > 0 && (
                <Card className="border-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Paso 3: Validación y Vista Previa</CardTitle>
                                <CardDescription>
                                    <span className="text-green-600">{validCount} válidos</span>
                                    {invalidCount > 0 && <span className="text-red-600 ml-2">{invalidCount} con errores</span>}
                                </CardDescription>
                            </div>
                            {validCount > 0 && !importResults && (
                                <Button onClick={handleImport} disabled={importing}>
                                    {importing ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Importando...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-4 w-4 mr-2" />
                                            Importar {validCount} registros
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {importing && (
                            <div className="mb-4">
                                <Progress value={importProgress} className="h-2" />
                                <p className="text-sm text-center mt-2">{importProgress}% completado</p>
                            </div>
                        )}
                        
                        {importResults && (
                            <div className="mb-4 p-4 rounded-lg bg-muted">
                                <h4 className="font-semibold mb-2">Resultado de la Importación</h4>
                                <div className="flex flex-wrap gap-3">
                                    <Badge className="bg-green-500">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        {importResults.success} nuevos
                                    </Badge>
                                    {importResults.updated > 0 && (
                                        <Badge className="bg-blue-500">
                                            <RefreshCw className="h-3 w-3 mr-1" />
                                            {importResults.updated} actualizados
                                        </Badge>
                                    )}
                                    {importResults.failed > 0 && (
                                        <Badge className="bg-red-500">
                                            <XCircle className="h-3 w-3 mr-1" />
                                            {importResults.failed} fallidos
                                        </Badge>
                                    )}
                                </div>
                                {importResults.errors.length > 0 && (
                                    <div className="mt-3 text-sm text-red-600 space-y-0.5">
                                        <p className="font-medium">Errores:</p>
                                        {importResults.errors.slice(0, 10).map((err, i) => (
                                            <p key={i}>{err}</p>
                                        ))}
                                        {importResults.errors.length > 10 && (
                                            <p>...y {importResults.errors.length - 10} errores más</p>
                                        )}
                                    </div>
                                )}
                                {importResults.skipped_details && importResults.skipped_details.length > 0 && (
                                    <div className="mt-3 text-sm text-blue-600 space-y-0.5">
                                        <p className="font-medium">Registros actualizados (ya existían):</p>
                                        {importResults.skipped_details.slice(0, 15).map((detail, i) => (
                                            <p key={i}>{detail}</p>
                                        ))}
                                        {importResults.skipped_details.length > 15 && (
                                            <p>...y {importResults.skipped_details.length - 15} más</p>
                                        )}
                                    </div>
                                )}
                                <div className="flex gap-2 mt-4">
                                    <Button variant="outline" onClick={resetImport}>
                                        Nueva Importación
                                    </Button>
                                    {lastImportIds.length > 0 && (
                                        <Button
                                            variant="destructive"
                                            onClick={handleRevertir}
                                            disabled={reverting}
                                            data-testid="revertir-btn"
                                        >
                                            {reverting ? (
                                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Revirtiendo...</>
                                            ) : (
                                                <><Undo2 className="h-4 w-4 mr-2" /> Deshacer Importación ({lastImportIds.length})</>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="max-h-96 overflow-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-16">Fila</TableHead>
                                        <TableHead className="w-20">Estado</TableHead>
                                        {config.fields.slice(0, 5).map(field => (
                                            <TableHead key={field}>{field}</TableHead>
                                        ))}
                                        <TableHead>Errores/Advertencias</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {validationResults.slice(0, 50).map((result) => (
                                        <TableRow key={result.row} className={!result.valid ? 'bg-red-50' : ''}>
                                            <TableCell>{result.row}</TableCell>
                                            <TableCell>
                                                {result.valid ? (
                                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                                ) : (
                                                    <XCircle className="h-5 w-5 text-red-500" />
                                                )}
                                            </TableCell>
                                            {config.fields.slice(0, 5).map(field => (
                                                <TableCell key={field} className="max-w-32 truncate">
                                                    {result.data[field] || '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-sm">
                                                {result.errors.map((e, i) => (
                                                    <span key={i} className="text-red-600 block">{e}</span>
                                                ))}
                                                {result.warnings.map((w, i) => (
                                                    <span key={i} className="text-yellow-600 block flex items-center gap-1">
                                                        <AlertTriangle className="h-3 w-3" />{w}
                                                    </span>
                                                ))}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {validationResults.length > 50 && (
                                <p className="text-center text-sm text-muted-foreground py-2">
                                    Mostrando 50 de {validationResults.length} filas
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
        </>
    );
}
